import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Form } from 'react-bootstrap';
import { CATEGORY_ICONS, getCategoryIconMeta } from '../../constants/categoryIcons';

const ICON_GROUPS = [
  'Todas',
  'Carnes y Charcutería',
  'Lácteos',
  'Abarrotes y Despensa',
  'Bebidas',
  'Panadería y Dulces',
  'Enlatados',
  'Congelados',
  'General',
];

const CategoryIconPicker = ({ value, onChange, isDark }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('Todas');
  const dropdownRef = useRef(null);

  const currentMeta = useMemo(() => {
    return getCategoryIconMeta(value);
  }, [value]);

  const hasIcon = Boolean(value);

  // Filtered list based on search and category
  const filteredIcons = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    return CATEGORY_ICONS.filter((item) => {
      const matchGroup = selectedGroup === 'Todas' || item.category === selectedGroup;
      const matchSearch =
        !term ||
        item.label.toLowerCase().includes(term) ||
        item.category.toLowerCase().includes(term) ||
        item.key.toLowerCase().includes(term);
      return matchGroup && matchSearch;
    });
  }, [searchTerm, selectedGroup]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const popoverBg = isDark ? '#1e293b' : '#ffffff';
  const popoverBorder = isDark ? '1px solid #334155' : '1px solid #e2e8f0';
  const itemDefaultBg = isDark ? '#0f172a' : '#f8fafc';
  const activeBg = isDark ? '#1e3a8a' : '#dbeafe';

  return (
    <div className="category-icon-picker-container position-relative" ref={dropdownRef}>
      {/* TRIGGER BUTTON (SELECCIÓN ACTUAL) */}
      <div
        className="d-flex align-items-center justify-content-between p-2 px-3 rounded"
        style={{
          border: isDark ? '1.5px solid #334155' : '1.5px solid #dcdfe6',
          backgroundColor: isDark ? '#0f172a' : '#f8fafc',
          transition: 'all 0.2s ease',
          minHeight: '46px',
        }}
      >
        <div
          onClick={() => setIsOpen(!isOpen)}
          className="d-flex align-items-center gap-3 overflow-hidden cursor-pointer flex-grow-1"
          title="Clic para seleccionar o cambiar el ícono"
        >
          <div
            className="d-flex align-items-center justify-content-center flex-shrink-0"
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '8px',
              backgroundColor: hasIcon ? '#183eeb' : (isDark ? '#334155' : '#e2e8f0'),
              color: hasIcon ? '#ffffff' : (isDark ? '#94a3b8' : '#64748b'),
              fontSize: '1.25rem',
            }}
          >
            <i className={`mdi ${currentMeta.mdi}`}></i>
          </div>
          <div className="text-truncate">
            <div className="fw-bold small" style={{ color: isDark ? '#f8fafc' : '#0f172a' }}>
              {hasIcon ? currentMeta.label : 'Sin Ícono (Predeterminado)'}
            </div>
            <div className="text-muted small" style={{ fontSize: '0.75rem' }}>
              {hasIcon ? (
                <>Grupo: <span className="fw-semibold" style={{ color: isDark ? '#94a3b8' : '#64748b' }}>{currentMeta.category}</span></>
              ) : (
                'Haz clic para asignar un ícono Material Design'
              )}
            </div>
          </div>
        </div>

        <div className="d-flex align-items-center gap-2 ms-2 flex-shrink-0">
          {hasIcon && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onChange('');
              }}
              className="btn btn-sm btn-outline-danger px-2 py-0"
              style={{ fontSize: '0.72rem', height: '26px', borderRadius: '6px' }}
              title="Quitar ícono de esta categoría"
            >
              <i className="mdi mdi-close me-1"></i>
              Quitar
            </button>
          )}

          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className="btn btn-sm px-2 py-0"
            style={{
              backgroundColor: isDark ? '#1e293b' : '#e2e8f0',
              color: isDark ? '#cbd5e1' : '#475569',
              fontSize: '0.72rem',
              fontWeight: '500',
              height: '26px',
              borderRadius: '6px',
            }}
          >
            {hasIcon ? 'Cambiar' : 'Elegir'}
            <i className={`mdi ${isOpen ? 'mdi-chevron-up' : 'mdi-chevron-down'} ms-1`}></i>
          </button>
        </div>
      </div>

      {/* DROPDOWN POPOVER */}
      {isOpen && (
        <div
          className="shadow-lg rounded p-3 position-absolute"
          style={{
            top: 'calc(100% + 6px)',
            left: 0,
            right: 0,
            zIndex: 1050,
            backgroundColor: popoverBg,
            border: popoverBorder,
            height: '370px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* BUSCADOR Y BOTÓN QUITAR */}
          <div className="mb-2 d-flex gap-2 align-items-center">
            <div className="position-relative flex-grow-1">
              <Form.Control
                size="sm"
                type="text"
                placeholder="Buscar ícono (ej: carnes, quesos, salsas, bebidas)..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
                style={{
                  paddingLeft: '34px',
                  borderRadius: '6px',
                  backgroundColor: isDark ? '#0f172a' : '#ffffff',
                  color: isDark ? '#ffffff' : '#000000',
                  borderColor: isDark ? '#475569' : '#cbd5e1',
                  height: '36px',
                  fontSize: '0.82rem',
                }}
              />
              <i
                className="mdi mdi-magnify position-absolute text-muted"
                style={{
                  left: '11px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  fontSize: '1.1rem',
                  lineHeight: 1,
                  pointerEvents: 'none',
                }}
              ></i>
            </div>

            {hasIcon && (
              <button
                type="button"
                onClick={() => {
                  onChange('');
                  setIsOpen(false);
                }}
                className="btn btn-sm btn-outline-secondary flex-shrink-0"
                style={{ height: '36px', borderRadius: '6px', fontSize: '0.78rem' }}
                title="Dejar la categoría sin ícono específico"
              >
                <i className="mdi mdi-close-circle-outline me-1"></i>
                Sin Ícono
              </button>
            )}
          </div>

          {/* SELECTORES DE GRUPO */}
          <div
            className="d-flex gap-2 align-items-center mb-2 pb-1"
            style={{
              overflowX: 'auto',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              padding: '2px 0',
              flexShrink: 0,
            }}
          >
            {ICON_GROUPS.map((group) => {
              const isGroupActive = selectedGroup === group;
              return (
                <button
                  type="button"
                  key={group}
                  onClick={() => setSelectedGroup(group)}
                  style={{
                    fontSize: '0.75rem',
                    fontWeight: isGroupActive ? '600' : '500',
                    padding: '0 12px',
                    borderRadius: '20px',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    height: '28px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    border: isGroupActive
                      ? '1px solid #183eeb'
                      : isDark
                      ? '1px solid #334155'
                      : '1px solid #e2e8f0',
                    backgroundColor: isGroupActive
                      ? '#183eeb'
                      : isDark
                      ? '#0f172a'
                      : '#f1f5f9',
                    color: isGroupActive
                      ? '#ffffff'
                      : isDark
                      ? '#94a3b8'
                      : '#475569',
                    transition: 'all 0.15s ease',
                    boxShadow: isGroupActive ? '0 2px 4px rgba(24, 62, 235, 0.25)' : 'none',
                  }}
                >
                  {group}
                </button>
              );
            })}
          </div>

          {/* CUADRÍCULA DE ÍCONOS */}
          <div
            className="overflow-auto pe-1"
            style={{
              flex: 1,
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
              gap: '8px',
              alignContent: 'start',
            }}
          >
            {filteredIcons.length === 0 ? (
              <div className="text-center py-4 text-muted small" style={{ gridColumn: '1 / -1' }}>
                <i className="mdi mdi-emoticon-sad-outline d-block h4 mb-1"></i>
                No se encontraron íconos para "{searchTerm}"
              </div>
            ) : (
              filteredIcons.map((item) => {
                const isSelected = item.key === currentMeta.key;
                return (
                  <div
                    key={item.key}
                    onClick={() => {
                      onChange(item.key);
                      setIsOpen(false);
                    }}
                    className="p-2 rounded cursor-pointer text-center d-flex flex-column align-items-center justify-content-center"
                    style={{
                      backgroundColor: isSelected ? activeBg : itemDefaultBg,
                      border: isSelected
                        ? '1.5px solid #183eeb'
                        : isDark
                        ? '1px solid #334155'
                        : '1px solid #e2e8f0',
                      transition: 'all 0.15s ease',
                      minHeight: '75px',
                    }}
                  >
                    <i
                      className={`mdi ${item.mdi} mb-1`}
                      style={{
                        fontSize: '1.4rem',
                        color: isSelected ? '#183eeb' : isDark ? '#f8fafc' : '#0f172a',
                      }}
                    ></i>
                    <div
                      className="text-truncate w-100 fw-semibold"
                      style={{
                        fontSize: '0.72rem',
                        color: isSelected ? '#183eeb' : isDark ? '#cbd5e1' : '#334155',
                      }}
                      title={item.label}
                    >
                      {item.label}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryIconPicker;
